import React, { useState } from 'react';
import { ChevronDown, Globe, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { TIMEZONE_OPTIONS, getTimezoneOptionsByGroup, getUserTimezone } from '@/utils/timezone';

interface TimezoneSelectorProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  showUseTimezone?: boolean;
  onUseTimezoneChange?: (use: boolean) => void;
}

export const TimezoneSelector: React.FC<TimezoneSelectorProps> = ({
  value = '',
  onChange,
  placeholder = 'Select timezone',
  disabled = false,
  className,
  showUseTimezone = true,
  onUseTimezoneChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [useTimezone, setUseTimezone] = useState(!!value);
  const [searchTerm, setSearchTerm] = useState('');

  const timezoneGroups = getTimezoneOptionsByGroup();
  const userTimezone = getUserTimezone();

  const filteredGroups = Object.entries(timezoneGroups).reduce((acc, [group, timezones]) => {
    const filtered = timezones.filter(tz => 
      tz.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tz.value.toLowerCase().includes(searchTerm.toLowerCase())
    );
    if (filtered.length > 0) {
      acc[group] = filtered;
    }
    return acc;
  }, {} as Record<string, typeof TIMEZONE_OPTIONS>);

  const handleTimezoneChange = (timezone: string) => {
    onChange?.(timezone);
    setIsOpen(false);
  };

  const handleUseTimezoneChange = (use: boolean) => {
    setUseTimezone(use);
    onUseTimezoneChange?.(use);
    if (!use) {
      onChange?.('');
    }
  };

  const selectedTimezone = TIMEZONE_OPTIONS.find(tz => tz.value === value);

  return (
    <div className={cn("space-y-3", className)}>
      {showUseTimezone && (
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="use-timezone"
            checked={useTimezone}
            onChange={(e) => handleUseTimezoneChange(e.target.checked)}
            disabled={disabled}
            className="rounded border-gray-300 text-primary focus:ring-primary/20"
          />
          <label htmlFor="use-timezone" className="text-sm font-medium text-gray-700">
            Use specific timezone
          </label>
        </div>
      )}

      {useTimezone && (
        <div className="relative">
          <button
            type="button"
            onClick={() => !disabled && setIsOpen(!isOpen)}
            disabled={disabled}
            className={cn(
              "w-full rounded border-l-[2.4px] border-primary px-5 py-3 flex justify-between items-center text-[#5A5A5A] placeholder:text-[#5A5A5A] bg-[#F6F9FF] text-sm outline-none transition-colors",
              disabled && "opacity-50 cursor-not-allowed",
              !disabled && "hover:bg-[#E8F2FF]"
            )}
          >
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4" />
              <span className={value ? "text-[#1A1A1A]" : "text-[#9CA3AF]"}>
                {selectedTimezone ? selectedTimezone.label : placeholder}
              </span>
            </div>
            <ChevronDown className="h-4 w-4" />
          </button>

          {isOpen && (
            <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-hidden">
              {/* Search */}
              <div className="p-3 border-b border-gray-200">
                <input
                  type="text"
                  placeholder="Search timezones..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>

              {/* Timezone Groups */}
              <div className="max-h-60 overflow-y-auto">
                {Object.entries(filteredGroups).map(([group, timezones]) => (
                  <div key={group} className="border-b border-gray-100 last:border-b-0">
                    <div className="px-3 py-2 bg-gray-50 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                      {group}
                    </div>
                    {timezones.map((timezone) => (
                      <button
                        key={timezone.value}
                        type="button"
                        onClick={() => handleTimezoneChange(timezone.value)}
                        className={cn(
                          "w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center justify-between",
                          value === timezone.value && "bg-primary/10 text-primary font-medium"
                        )}
                      >
                        <div>
                          <div className="font-medium">{timezone.label}</div>
                          <div className="text-xs text-gray-500">{timezone.value}</div>
                        </div>
                        <div className="text-xs text-gray-400 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {timezone.offset}
                        </div>
                      </button>
                    ))}
                  </div>
                ))}
              </div>

              {/* Quick Actions */}
              <div className="p-3 border-t border-gray-200 bg-gray-50">
                <button
                  type="button"
                  onClick={() => handleTimezoneChange(userTimezone)}
                  className="w-full text-left text-sm text-primary hover:text-primary/80 font-medium"
                >
                  Use my timezone ({userTimezone})
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
